import React from 'react';

/**
 * SkeletonLoader — Reusable skeleton/shimmer loader components
 * Used across sidebar, overview, candidate detail, and message sections.
 */

/** Base shimmer bar */
const SkeletonBar = ({ className = '', style = {} }) => (
  <div
    className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`}
    style={style}
  />
);

/** Sidebar job posting list skeleton */
export const SidebarSkeleton = () => (
  <div className="space-y-3 px-4 py-2">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-3 py-2">
        <SkeletonBar className="w-8 h-8 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <SkeletonBar className="h-3.5 w-3/4" />
          <SkeletonBar className="h-2.5 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

/** Overview section skeleton — funnel + charts + calendar placeholder */
export const OverviewSkeleton = () => (
  <div className="p-6 space-y-6">
    {/* Title bar */}
    <div className="flex items-center justify-between">
      <SkeletonBar className="h-5 w-48" />
      <SkeletonBar className="h-8 w-24 rounded-lg" />
    </div>

    {/* Funnel bars */}
    <div className="grid grid-cols-6 gap-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="space-y-2 text-center">
          <SkeletonBar className="h-20 w-full rounded-lg" />
          <SkeletonBar className="h-3 w-16 mx-auto" />
        </div>
      ))}
    </div>

    {/* Channel performance + Responses over time row */}
    <div className="grid grid-cols-2 gap-6">
      <div className="space-y-3">
        <SkeletonBar className="h-4 w-32" />
        <SkeletonBar className="h-32 w-full rounded-lg" />
      </div>
      <div className="space-y-3">
        <SkeletonBar className="h-4 w-40" />
        <SkeletonBar className="h-32 w-full rounded-lg" />
      </div>
    </div>

    {/* Calendar placeholder */}
    <div className="space-y-3">
      <SkeletonBar className="h-4 w-36" />
      <SkeletonBar className="h-48 w-full rounded-lg" />
    </div>
  </div>
);

/** Candidate detail skeleton — header + info cards + timeline */
export const CandidateDetailSkeleton = () => (
  <div className="p-6 space-y-6">
    {/* Breadcrumb */}
    <SkeletonBar className="h-3.5 w-48" />

    {/* Header */}
    <div className="flex items-center gap-4">
      <SkeletonBar className="w-16 h-16 rounded-full" />
      <div className="space-y-2 flex-1">
        <SkeletonBar className="h-5 w-48" />
        <SkeletonBar className="h-3.5 w-64" />
        <SkeletonBar className="h-3 w-32" />
      </div>
    </div>

    {/* Info cards */}
    <div className="grid grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
          <SkeletonBar className="h-3 w-20" />
          <SkeletonBar className="h-4 w-28" />
        </div>
      ))}
    </div>

    {/* Detail sections */}
    <div className="grid grid-cols-2 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
          <SkeletonBar className="h-4 w-24" />
          <SkeletonBar className="h-3 w-full" />
          <SkeletonBar className="h-3 w-3/4" />
          <SkeletonBar className="h-3 w-5/6" />
        </div>
      ))}
    </div>

    {/* Timeline */}
    <div className="space-y-4">
      <SkeletonBar className="h-4 w-40" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3">
          <SkeletonBar className="w-2 h-16 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonBar className="h-3.5 w-48" />
            <SkeletonBar className="h-3 w-full" />
            <SkeletonBar className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Message history skeleton — timeline with dots and cards (matches Conversation History UI) */
export const MessageHistorySkeleton = () => (
  <div className="relative">
    {/* Vertical timeline line */}
    <div className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700" />

    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="relative flex items-start gap-4 pl-0">
          {/* Timeline dot */}
          <div className="relative z-10 flex-shrink-0 flex items-center justify-center">
            <SkeletonBar className="w-[30px] h-[30px] rounded-full" />
          </div>

          {/* Event card with left border */}
          <div className="flex-1 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden" style={{ borderLeft: '4px solid #E5E7EB' }}>
            <div className="px-4 py-3 space-y-2">
              {/* Header row: label + date */}
              <div className="flex items-center justify-between">
                <SkeletonBar className="h-3.5 w-28" />
                <SkeletonBar className="h-2.5 w-20" />
              </div>
              {/* Subject line */}
              <SkeletonBar className="h-2.5 w-48" />
              {/* Content preview */}
              <div className="space-y-1.5 mt-1">
                <SkeletonBar className="h-3 w-full" />
                <SkeletonBar className="h-3 w-4/5" />
                <SkeletonBar className="h-3 w-2/3" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Page-level loading overlay — subtle full-page spinner */
export const PageLoadingOverlay = () => (
  <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 rounded-full animate-spin" style={{ border: '3px solid #D1D5DB', borderTopColor: '#3B82F6' }} />
      <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
    </div>
  </div>
);

export default SkeletonBar;
